import { ChevronUpIcon, PencilIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { formatDate } from "../../helpers/formatDate";
import {
    formatDateTime,
    formatTimestampDate,
} from "../../helpers/formatDateTime";
import { getBgColorFromDate } from "../../helpers/getBgColorFromDate";
import {
    getClientServiceIntervalConfig,
    normalizeClientServiceInterval,
} from "../../helpers/intervalTypesMap";
import EfileCompletionConfirmationModal from "../../components/clientService/EfileCompletionConfirmationModal";
import { renewClientServiceSubscription } from "../../services/clientService/renewClientServiceSubscription";
import { updateSubscriptionEfileStatus } from "../../services/clientService/updateClientServiceEFileStatus";
import { createClientServiceSubscriptionTask } from "../../services/clientServiceSubscriptionTask/createClientServiceSubscriptionTask";
import { getClientServiceSubscriptionTasksByClientServiceId } from "../../services/clientServiceSubscriptionTask/getClientServiceSubscriptionTasksByServiceId";
import { updateTaskService } from "../../services/clientServiceSubscriptionTask/updateTaskService";
import { getClientServiceSubscriptionTaskChecklistItemByTaskId } from "../../services/clientServiceSubscriptionTaskChecklistItem/getClientServiceSubscriptionTaskChecklistItemByTaskId";
import { updateClientServiceSubscriptionTaskChecklistItemCheck } from "../../services/clientServiceSubscriptionTaskChecklistItem/updateClientServiceSubscriptionTaskChecklistItemCheck";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import { renewSubscriptionTask } from "../../services/renewSubscriptionTask";
import { getSubscriptionTeamMembers } from "../../services/clientServiceSubscription/getSubscriptionTeamMembers";
import { term } from "../../terms";
import { shouldPromptForEfileCompletion } from "../../helpers/eFileStatus";
import AddNewClientServiceSubscriptionTask from "./addNewClientSubscriptionTaskModal/AddNewClientSubscriptionTask";
import ClientServiceSubscriptionTaskChecklistItemTableBody from "./checklistItems/ClientServiceSubscriptionTaskChecklistItemTableBody";
import AssignNewEmployeeToJobModal from "./updateClientServiceSubscriptionTaskModal/AssignNewEmployeeToJobModal";
import ServiceStatusesListBox from "./updateClientServiceSubscriptionTaskModal/ServiceStatusesListBox";
import StartJobModal from "./updateClientServiceSubscriptionTaskModal/StartJobModal";
import UpdateClientServiceSubscriptionTaskModal from "./updateClientServiceSubscriptionTaskModal/UpdateClientServiceSubscriptionTaskModal";
import StepEmployeeDetails from "./StepEmployeeDetails";
import { useCelebration } from "../../context/CelebrationContext";
import {
    getStrictlyLaterStep,
    shouldCelebrateStepCompletion,
} from "../../helpers/completionCelebration";
import { getClientServiceMessage } from "../../helpers/clientServiceMessage";

const ONE_TIME_INTERVAL = "One-time";
const ACCEPTED_EFILE_STATUS = 2;
const EFILE_CONFIRM_STEP = "efile";
const COMPLETE_ONLY_STEP = "completeOnly";
const STATUSES = ["To Do", "In Progress", "Complete"];

const STATUSES_IN_FILTER = [
    { id: -1, state: "All" },
    { id: 0, state: "To Do" },
    { id: 1, state: "In Progress" },
];

const RENEWAL_STATE = ["None", "Renew", "Renewed"];

const NOTES_TOOLTIP_WIDTH = 460;
const NOTES_TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 8;

export const StepNotesPreview = ({ notes }) => {
    const textRef = useRef(null);
    const tooltipRef = useRef(null);
    const [isOverflow, setIsOverflow] = useState(false);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState(null);

    useEffect(() => {
        const checkOverflow = () => {
            if (!textRef.current) return;

            const nextIsOverflow =
                textRef.current.scrollHeight > textRef.current.clientHeight;

            setIsOverflow(nextIsOverflow);
            if (!nextIsOverflow) setIsTooltipVisible(false);
        };

        checkOverflow();
        window.addEventListener("resize", checkOverflow);

        return () => {
            window.removeEventListener("resize", checkOverflow);
        };
    }, [notes]);

    const updateTooltipPosition = useCallback(() => {
        if (!textRef.current || !tooltipRef.current) return;

        const previewRect = textRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const width = Math.min(
            NOTES_TOOLTIP_WIDTH,
            Math.max(1, viewportWidth - VIEWPORT_PADDING * 2),
        );
        const maximumLeft = Math.max(
            VIEWPORT_PADDING,
            viewportWidth - width - VIEWPORT_PADDING,
        );
        const left = Math.min(
            Math.max(previewRect.left, VIEWPORT_PADDING),
            maximumLeft,
        );
        const tooltipHeight = tooltipRef.current.getBoundingClientRect().height;
        const belowTop = previewRect.bottom + NOTES_TOOLTIP_GAP;
        const aboveTop = previewRect.top - tooltipHeight - NOTES_TOOLTIP_GAP;
        let top = belowTop;

        if (
            belowTop + tooltipHeight > viewportHeight - VIEWPORT_PADDING &&
            aboveTop >= VIEWPORT_PADDING
        ) {
            top = aboveTop;
        } else if (
            belowTop + tooltipHeight >
            viewportHeight - VIEWPORT_PADDING
        ) {
            top = Math.max(
                VIEWPORT_PADDING,
                viewportHeight - tooltipHeight - VIEWPORT_PADDING,
            );
        }

        setTooltipPosition({ left, top, width });
    }, []);

    useLayoutEffect(() => {
        if (!isTooltipVisible || !isOverflow) return;
        updateTooltipPosition();
    }, [isOverflow, isTooltipVisible, updateTooltipPosition]);

    useEffect(() => {
        if (!isTooltipVisible || !isOverflow) return undefined;

        window.addEventListener("resize", updateTooltipPosition);
        window.addEventListener("scroll", updateTooltipPosition, true);

        return () => {
            window.removeEventListener("resize", updateTooltipPosition);
            window.removeEventListener("scroll", updateTooltipPosition, true);
        };
    }, [isOverflow, isTooltipVisible, updateTooltipPosition]);

    if (!notes || notes.trim() === "") {
        return <span className="text-sm font-normal text-slate-400">-</span>;
    }

    return (
        <div
            className="relative min-w-0 w-full max-w-[460px]"
            onMouseEnter={() => {
                if (isOverflow) setIsTooltipVisible(true);
            }}
            onMouseLeave={() => setIsTooltipVisible(false)}
        >
            <div
                ref={textRef}
                className="overflow-hidden text-sm font-normal leading-5 text-left break-words whitespace-pre-wrap text-slate-700"
                style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: "vertical",
                }}
            >
                {notes}
            </div>

            {isOverflow &&
                isTooltipVisible &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        role="tooltip"
                        className="pointer-events-none fixed z-[100] rounded-md border border-slate-200 bg-white p-3 text-sm font-normal leading-5 text-slate-700 text-left shadow-xl"
                        style={{
                            left: tooltipPosition?.left ?? 0,
                            top: tooltipPosition?.top ?? 0,
                            width:
                                tooltipPosition?.width ?? NOTES_TOOLTIP_WIDTH,
                            visibility: tooltipPosition ? "visible" : "hidden",
                        }}
                    >
                        <div className="overflow-auto text-left break-words whitespace-pre-wrap max-h-64">
                            {notes}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

const ClientServiceSubscriptionTasksList = ({
    clientServiceId,
    onHandleUpdateClientServiceStatusComplete,
    subscriptionInterval,
    isServiceStatusInProgress,
    clientService,
    updateClientService,
    clientId,
    canReadSteps,
    canWriteSteps,
    canDeleteSteps,
    canWriteServices,
}) => {
    const { celebrate, showCompletionMessage } = useCelebration();
    const [tasks, setTasks] = useState([]);
    const [incompleteTasks, setIncompleteTasks] = useState([]);
    const [completedTasks, setCompletedTasks] = useState([]);
    const [updateTaskModal, setUpdateTaskModal] = useState({});
    const [isUpdateTaskModalOpen, setIsUpdateTaskModalOpen] = useState(false);
    const [isAddNewTaskModalOpen, setIsAddNewTaskModalOpen] = useState(false);
    const [loggedInEmployee, setLoggedInEmployee] = useState({});
    const [subscriptionTeamMembers, setSubscriptionTeamMembers] = useState([]);
    const [selectedFilterStatus, setSelectedFilterStatus] = useState({});
    const [assignNextJobModalOpen, setAssignNextJobModalOpen] = useState(false);
    const [nextJobToAssign, setNextJobToAssign] = useState({});
    const [checklistItemToUpdate, setChecklistItemToUpdate] = useState({});
    const [currentJob, setCurrentJob] = useState({});
    const [startJobModal, setStartJobModal] = useState(false);
    const [jobToStart, setJobToStart] = useState(null);
    const [shouldResetChecklist, setShouldResetChecklist] = useState(false);
    const [isServiceRenewing, setIsServiceRenewing] = useState(false);
    const [isEfileCompletionModalOpen, setIsEfileCompletionModalOpen] =
        useState(false);
    const [efileCompletionStep, setEfileCompletionStep] =
        useState(EFILE_CONFIRM_STEP);
    const [pendingCompletionCallback, setPendingCompletionCallback] = useState(null);
    const [isCompletingService, setIsCompletingService] = useState(false);
    const [isUpdatingEfile, setIsUpdatingEfile] = useState(false);

    const handleStepCompletionCelebration = (
        response,
        completedStep,
        assignedNextStep,
    ) => {
        const nextStep =
            assignedNextStep ?? getStrictlyLaterStep(tasks, completedStep);
        const shouldCelebrateStepHandoff = shouldCelebrateStepCompletion(
            response,
            completedStep,
            nextStep,
        );

        if (!shouldCelebrateStepHandoff) return;

        celebrate();
        showCompletionMessage("Congratulations on completing your steps");
    };

    const isTaskValidToRenew = async (clientServiceSubscriptionTaskId) => {
        if (!canReadSteps || !canWriteSteps) return false;

        const checklistItems =
            await getClientServiceSubscriptionTaskChecklistItemByTaskId(
                clientServiceSubscriptionTaskId,
            );
        const numberOfUncheckedItems =
            checklistItems.data.checklistItemsResponse.filter(
                (item) => item.isChecked === false,
            ).length;
        return numberOfUncheckedItems === 0;
    };

    const calculateNewRenewalDate = (date) => {
        // Guardrail: one-time never renews
        const normalizedInterval =
            normalizeClientServiceInterval(subscriptionInterval);

        if (!normalizedInterval || normalizedInterval === ONE_TIME_INTERVAL) {
            return null;
        }

        const base = date instanceof Date ? date : new Date(date);
        if (Number.isNaN(base.getTime())) return null;

        const interval = getClientServiceIntervalConfig(normalizedInterval);

        const daysToAdd = interval?.days;

        // Guardrail: if interval has no valid days, do not calculate
        if (!Number.isFinite(daysToAdd) || daysToAdd <= 0) return null;

        const newDate = new Date(base);
        newDate.setDate(newDate.getDate() + daysToAdd);

        if (Number.isNaN(newDate.getTime())) return null;

        return newDate;
    };

    async function renewTask(subscriptionId, taskId, assignedDate) {
        if (!canWriteSteps) return;

        const isChecklistItemsChecked = await isTaskValidToRenew(taskId);
        if (!isChecklistItemsChecked) {
            toast.error("Check all the checklist items before renewing.");
            return;
        }

        const assignedDateCalculated = calculateNewRenewalDate(assignedDate);
        const assignedDateCalculatedISO = assignedDateCalculated
            ? assignedDateCalculated.toISOString()
            : null;

        const newAssignedDate = assignedDateCalculatedISO;
        const payload = {
            subscriptionId,
            taskId,
            assignedDate: newAssignedDate,
            dueDate: newAssignedDate,
        };

        console.log(payload.dueDate.getFullYear());
        console.log(clientService.year);

        if (payload.dueDate.getFullYear() > clientService.year) {
            toast.error(
                "Renewal was not possible since it exceeds the service year",
            );
            await getNewTasks();
            return;
        }

        const res = await renewSubscriptionTask(payload);
        if (res.success) {
            await getNewTasks();
        } else {
            alert(getClientServiceMessage(res.message));
        }
    }

    const updateTasks = async () => {
        if (!canReadSteps) return;
        await getNewTasks();
        setIsUpdateTaskModalOpen(false);
    };

    const IsEveryJobComplete = (taskId) => {
        return tasks
            .filter((task) => task.id !== taskId)
            .every((task) => task.status === 2);
    };

    const updateChecklistItemAndJob = async (
        nextJob = nextJobToAssign,
        actualJob = currentJob,
        checklistItem = checklistItemToUpdate,
    ) => {
        if (!canWriteSteps) return;

        const updateNextJobResponse = await updateTaskService(nextJob.id, nextJob);
        if (!updateNextJobResponse.data.success) {
            toast.error(updateNextJobResponse.data.message);
            return;
        }

        const updateChecklistItemResponse =
            await updateClientServiceSubscriptionTaskChecklistItemCheck(
                checklistItem.id,
                true,
            );
        if (!updateChecklistItemResponse.success) {
            toast.error(updateChecklistItemResponse.data.message);
            return;
        }

        const updateCurrentJobResponse = await updateTaskService(
            actualJob.id,
            actualJob,
        );
        if (updateCurrentJobResponse.data.success) {
            handleStepCompletionCelebration(
                updateCurrentJobResponse,
                actualJob,
                nextJob,
            );
            toast.success("Steps updated successfully!");
            await getNewTasks();
        } else {
            toast.error(updateCurrentJobResponse.data.message);
        }

        initializeJobs();
        setAssignNextJobModalOpen(false);
    };

    const initializeJobs = () => {
        setCurrentJob({});
        setNextJobToAssign({});
    };

    const clearEfileCompletionModal = () => {
        setIsEfileCompletionModalOpen(false);
        setEfileCompletionStep(EFILE_CONFIRM_STEP);
        setPendingCompletionCallback(null);
    };

    const runPendingCompletion = async () => {
        if (
            !canWriteSteps ||
            !canWriteServices ||
            !pendingCompletionCallback ||
            isCompletingService
        )
            return;

        setIsCompletingService(true);
        try {
            await pendingCompletionCallback();
            clearEfileCompletionModal();
        } finally {
            setIsCompletingService(false);
        }
    };

    const completeServiceWithEfilePrompt = async (completionCallback) => {
        if (
            !canWriteSteps ||
            !canWriteServices ||
            isCompletingService ||
            isUpdatingEfile
        )
            return;

        const shouldShowEfileCompletionModal =
            shouldPromptForEfileCompletion(clientService);

        if (!shouldShowEfileCompletionModal) {
            setIsCompletingService(true);
            try {
                await completionCallback();
            } finally {
                setIsCompletingService(false);
            }
            return;
        }

        setPendingCompletionCallback(() => completionCallback);
        setEfileCompletionStep(EFILE_CONFIRM_STEP);
        setIsEfileCompletionModalOpen(true);
    };

    const onHandleUpdateEfileAndComplete = async () => {
        if (
            !canWriteSteps ||
            !canWriteServices ||
            !pendingCompletionCallback ||
            isUpdatingEfile ||
            isCompletingService
        ) {
            return;
        }

        setIsUpdatingEfile(true);
        try {
            const response = await updateSubscriptionEfileStatus(
                clientServiceId,
                ACCEPTED_EFILE_STATUS,
            );

            if (!response.success) {
                toast.error(response.message);
                setShouldResetChecklist(true);
                return;
            }

            await runPendingCompletion();
        } finally {
            setIsUpdatingEfile(false);
        }
    };

    const onHandleCancelEfileCompletion = () => {
        if (isCompletingService || isUpdatingEfile) return;

        clearEfileCompletionModal();
        setShouldResetChecklist(true);
        initializeJobs();
    };

    const onUpdateAllChecklistItemsV2 = async (task, item, options = {}) => {
        if (!canWriteSteps) return;

        console.log("tasks", tasks);
        console.log("task", task);
        const bypassAutoUpdate = options.bypassAutoUpdate || false;

        if (IsEveryJobComplete(task.id) && clientService.isEveryItemRenewable) {
            await renewServiceAutomatically(task, item);
            return;
        }

        // find next job that is in To Do (status 0) or In Progress (1, but not completed)
        let nextJob = tasks.find((t) => t.order > task.order && t.status !== 2);

        // if there is no next job, find the previous job that is not completed
        if (!nextJob) {
            nextJob = tasks.find((t) => t.order < task.order && t.status !== 2);
        }

        console.log("nextJob", nextJob);

        if (!bypassAutoUpdate) {
            const currentJobAssigned = {
                ...task,
                status: 2,
                completedBy: loggedInEmployee,
                completedDate: moment().format("YYYY-MM-DD"),
            };

            if (nextJob) {
                const nextJobToAssign = {
                    ...nextJob,
                    assignedByEmployee: loggedInEmployee,
                    status: 1,
                    dueDate: new Date().toISOString().split("T")[0],
                };
                setNextJobToAssign(nextJobToAssign);
                setCurrentJob(currentJobAssigned);
                setChecklistItemToUpdate(item);

                if (
                    !nextJob.assignedEmployees ||
                    nextJob.assignedEmployees.length < 1
                ) {
                    setAssignNextJobModalOpen(true);
                } else {
                    await updateChecklistItemAndJob(
                        nextJobToAssign,
                        currentJobAssigned,
                        item,
                    );
                    initializeJobs();
                }
            } else {
                await completeServiceWithEfilePrompt(async () => {
                    initializeJobs();
                    const stepUpdated = await updateJobStatus(currentJobAssigned, {
                        showSuccessToast: false,
                    });
                    if (!stepUpdated) return;

                    const checklistUpdated = await updateChecklistItem(item, {
                        showSuccessToast: false,
                    });
                    if (!checklistUpdated) return;

                    await onHandleUpdateClientServiceStatusComplete({
                        showSuccessToast: false,
                    });
                });
            }
        }
    };

    const AddNewTask = async (newTask) => {
        if (!canWriteSteps) return;

        const response = await createClientServiceSubscriptionTask(
            clientServiceId,
            newTask,
        );
        console.log(clientServiceId);
        if (response.data.success) {
            toast.success(response.data.message);
            await getNewTasks();
            setIsAddNewTaskModalOpen(false);
        } else {
            toast.error(response.data.message);
        }
    };

    const getNewLoggedInEmployee = async () => {
        if (!canReadSteps) return;

        const currentEmployee = await getLoggedInEmployee();
        if (currentEmployee.success) {
            setLoggedInEmployee(currentEmployee.data.employeeResponse);
            console.log(currentEmployee);
        }
    };

    const getNewSubscriptionTeamMembers = async () => {
        if (!canReadSteps) return;

        const response = await getSubscriptionTeamMembers(clientServiceId);
        setSubscriptionTeamMembers(
            response.success && Array.isArray(response.data?.teamMembers)
                ? response.data.teamMembers
                : [],
        );
    };

    const getNewTasks = async (taskOpen = 0) => {
        if (!canReadSteps) return [];

        console.log("Task that should be open", taskOpen);
        const { data: newTasks } =
            await getClientServiceSubscriptionTasksByClientServiceId(
                clientServiceId,
            );

        const allTasks = newTasks.tasksResponse ?? [];
        setTasks(allTasks);
        console.log("jobs response", allTasks);

        const completedJobs = allTasks.filter((task) => task.status === 2);
        setCompletedTasks(completedJobs);

        const incompleteJobsList = allTasks.filter((task) => task.status !== 2);

        // Ensure isOpen is set on the correct task
        const updatedIncomplete = incompleteJobsList.map((t) => ({
            ...t,
            isOpen: false,
        }));

        if (updatedIncomplete.length > 0) {
            updatedIncomplete[0].isOpen = true;
            if (taskOpen > 0) {
                const indexToOpen = updatedIncomplete.findIndex(
                    (task) => task.order === taskOpen,
                );
                if (indexToOpen !== -1) {
                    updatedIncomplete[indexToOpen].isOpen = true;
                }
            }
        }

        setIncompleteTasks(updatedIncomplete);
        setSelectedFilterStatus(STATUSES_IN_FILTER[0]);

        return allTasks;
    };

    const onHandleFilterStatusV2 = (currentStatus) => {
        setSelectedFilterStatus(currentStatus);
        let newIncompleteJobs;
        if (currentStatus.id !== -1) {
            newIncompleteJobs = tasks.filter(
                (task) => task.status === currentStatus.id,
            );
        } else {
            newIncompleteJobs = tasks.filter((task) => task.status !== 2);
        }
        setIncompleteTasks(
            newIncompleteJobs.map((t) => ({
                ...t,
                isOpen: t.isOpen ?? false,
            })),
        );
    };

    const updateJobStatus = async (
        job,
        { showSuccessToast = true } = {},
    ) => {
        if (!canWriteSteps) return false;

        const response = await updateTaskService(
            job.id,
            job,
            loggedInEmployee.employeeId,
        );
        if (response?.data?.success) {
            handleStepCompletionCelebration(response, job);
            if (showSuccessToast) {
                toast.success(response.data.message);
            }
            await updateTasks();
            return true;
        } else {
            toast.error(
                response?.data?.message ||
                    response?.message ||
                    `Error while updating ${term("step_singular").toLowerCase()}.`,
            );
            return false;
        }
    };

    const updateChecklistItem = async (
        item,
        { showSuccessToast = true } = {},
    ) => {
        if (!canWriteSteps) return false;

        const response = await updateClientServiceSubscriptionTaskChecklistItemCheck(
            item.id,
            true,
        );
        if (response.success === true) {
            await updateTasks();
            if (showSuccessToast) {
                toast.success("Checklist item updated!");
            }
            return true;
        } else {
            toast.error("Error while updating checklist item.");
            return false;
        }
    };

    const runAutomaticServiceRenewalCompletion = async (task, item, dueDate) => {
        if (!canWriteSteps || !canWriteServices || isServiceRenewing) return;

        setIsServiceRenewing(true); // Blocks everything while saving

        try {
            // 1) Try to renew the service first
            const renewResponse = await renewClientServiceSubscription(
                dueDate,
                clientServiceId,
            );

            if (!renewResponse.success) {
                console.log("aqui we", renewResponse);
                // Renewal failed → do NOT complete subscription, do NOT check checklist
                toast.error(
                    getClientServiceMessage(
                        renewResponse?.message,
                        "Could not renew service.",
                    ),
                );
                return;
            }

            // 2) Only after a successful renewal, we can safely close checklist and job
            const currentJobAssigned = {
                ...task,
                status: 2,
                completedBy: loggedInEmployee,
                completedDate: moment().format("YYYY-MM-DD"),
            };

            // Optional: keep whatever UI reset you need
            initializeJobs();

            // Checklist gets checked only if renewal succeeded
            const checklistUpdated = await updateChecklistItem(item, {
                showSuccessToast: false,
            });
            if (!checklistUpdated) return;

            // Job is marked as completed only if renewal succeeded
            const stepUpdated = await updateJobStatus(currentJobAssigned, {
                showSuccessToast: false,
            });
            if (!stepUpdated) return;

            // Now we mark the current client service as complete
            await onHandleUpdateClientServiceStatusComplete({
                showSuccessToast: false,
            });
        } catch (error) {
            console.error("Error during renewal process:", error);
            toast.error("An unexpected error occurred during renewal.");
        } finally {
            setIsServiceRenewing(false); // Re-enable UI regardless of success or failure
        }
    };

    const renewServiceAutomatically = async (task, item) => {
        if (!canWriteSteps || !canWriteServices || isServiceRenewing) return;

        const nextRenewalDate = calculateNewRenewalDate(clientService.endDate);
        const dueDate = nextRenewalDate
            ? nextRenewalDate.toISOString().split("T")[0]
            : null;

        if (!dueDate) {
            toast.error("Could not calculate renewal due date.");
            return;
        }

        await completeServiceWithEfilePrompt(() =>
            runAutomaticServiceRenewalCompletion(task, item, dueDate),
        );
    };

    const onStartJob = async (job) => {
        if (!canWriteSteps) return;

        // Refresh list from server and use the fresh array
        const freshTasks = await getNewTasks();
        const freshJob = freshTasks.find((t) => t.id === job.id) ?? job;
        setJobToStart(freshJob);
        setStartJobModal(true);
    };

    useEffect(() => {
        if (!canReadSteps) return;
        getNewLoggedInEmployee();
        getNewSubscriptionTeamMembers();
        getNewTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isServiceStatusInProgress, clientService.status, canReadSteps]);

    if (!canReadSteps) return null;

    return (
        <div className="flex flex-col w-full space-y-8">
            <section>
                {canWriteSteps && (
                    <AddNewClientServiceSubscriptionTask
                        open={isAddNewTaskModalOpen}
                        setOpen={setIsAddNewTaskModalOpen}
                        AddNewTask={AddNewTask}
                        clientServiceId={clientServiceId}
                        canWriteSteps={canWriteSteps}
                    />
                )}
                {canWriteSteps && (
                    <UpdateClientServiceSubscriptionTaskModal
                        updateTaskModal={updateTaskModal}
                        setUpdateTaskModal={setUpdateTaskModal}
                        open={isUpdateTaskModalOpen}
                        setOpen={setIsUpdateTaskModalOpen}
                        loggedInEmployee={loggedInEmployee}
                        updateTasks={updateTasks}
                        clientServiceId={clientServiceId}
                        updateClientService={updateClientService}
                        initializeJobs={initializeJobs}
                        clientId={clientId}
                        canWriteSteps={canWriteSteps}
                    />
                )}
                {canWriteSteps && (
                    <AssignNewEmployeeToJobModal
                    currentJob={currentJob}
                    setCurrentJob={setCurrentJob}
                    nextJob={nextJobToAssign}
                    setNextjob={setNextJobToAssign}
                    open={assignNextJobModalOpen}
                    setOpen={setAssignNextJobModalOpen}
                    getNewTasks={getNewTasks}
                    checklistItemToUpdate={checklistItemToUpdate}
                    clientService={clientService}
                    renewTask={renewTask}
                    loggedInEmployee={loggedInEmployee}
                    updateChecklistItemAndJob={updateChecklistItemAndJob}
                    initializeJobs={initializeJobs}
                    onHandleCancelFromParent={() => {
                        // UI must go back to its original state after cancel
                        setShouldResetChecklist(true); // tells the checklist table to visually un-toggle any item
                        initializeJobs(); // clear pending current/next jobs
                    }}
                    canWriteSteps={canWriteSteps}
                    />
                )}
                {canWriteSteps && canWriteServices && (
                    <EfileCompletionConfirmationModal
                    open={isEfileCompletionModalOpen}
                    step={efileCompletionStep}
                    isCompletingService={isCompletingService}
                    isUpdatingEfile={isUpdatingEfile}
                    onUpdateAndComplete={onHandleUpdateEfileAndComplete}
                    onCompleteOnly={() => setEfileCompletionStep(COMPLETE_ONLY_STEP)}
                    onConfirmCompleteOnly={runPendingCompletion}
                    onGoBack={() => setEfileCompletionStep(EFILE_CONFIRM_STEP)}
                    onCancel={onHandleCancelEfileCompletion}
                    />
                )}
                {canWriteSteps && (
                    <StartJobModal
                    open={startJobModal}
                    setOpen={setStartJobModal}
                    nextJob={jobToStart}
                    setNextjob={setJobToStart}
                    getNewJobs={getNewTasks}
                    initializeJobs={initializeJobs}
                    updateChecklistItemAndJob={updateChecklistItemAndJob}
                    updateTasks={updateTasks}
                    loggedInEmployee={loggedInEmployee}
                    clientId={clientId}
                    clientServiceSubscriptionId={clientServiceId}
                    canWriteSteps={canWriteSteps}
                    />
                )}
            </section>

            <div className="w-full mt-12 mb-4 sm:flex sm:justify-between sm:items-center">
                <div className="flex items-center justify-between w-full mb-4 sm:mb-0">
                    <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                        {term("step_plural")}
                    </h1>
                    {/* If you want to enable adding custom tasks again, use this button */}
                    {/* {isServiceStatusInProgress && (
                        <button
                            onClick={() => setIsAddNewTaskModalOpen(true)}
                            className="h-10 text-white bg-teal-500 btn hover:bg-teal-600"
                            disabled
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span className="ml-2 xs:block">
                                Add Custom {term("step_singular")}
                            </span>
                        </button>
                    )} */}
                </div>
            </div>

            <h2 className="text-lg font-bold text-yellow-500">
                Incomplete {term("step_plural")}
            </h2>
            <div className="sm:w-1/2 lg:w-1/4">
                <ServiceStatusesListBox
                    label="Filter by Status"
                    selectedServiceStatus={selectedFilterStatus}
                    serviceStatuses={STATUSES_IN_FILTER}
                    onChange={onHandleFilterStatusV2}
                />
            </div>

            {incompleteTasks &&
                incompleteTasks.map((task, index) => {
                    return (
                        <div
                            key={task.id ?? index}
                            className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200"
                        >
                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: "auto" }}
                                exit={{ height: 0 }}
                                transition={{
                                    type: "linear",
                                    height: { duration: 0.2 },
                                }}
                                className={"overflow-x-auto overflow-y-hidden"}
                            >
                                <div className="w-full overflow-x-auto sm:overflow-visible">
                                    <table className="w-full table-fixed">
                                        <thead>
                                            <tr>
                                                {/* Main column: toggle + title + status + renew */}
                                                <th className="py-3 align-top first:pl-5">
                                                    <div className="flex flex-wrap items-start gap-2">
                                                        {/* Toggle button */}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const newTasks = [
                                                                    ...incompleteTasks,
                                                                ];
                                                                newTasks[index] = {
                                                                    ...newTasks[
                                                                        index
                                                                    ],
                                                                    isOpen: !newTasks[
                                                                        index
                                                                    ].isOpen,
                                                                };
                                                                setIncompleteTasks(
                                                                    newTasks,
                                                                );
                                                            }}
                                                            className="inline-flex items-center justify-center w-8 h-8 border rounded-md border-slate-200 hover:bg-slate-50 shrink-0"
                                                            aria-label={
                                                                task.isOpen
                                                                    ? "Collapse checklist"
                                                                    : "Expand checklist"
                                                            }
                                                        >
                                                            <ChevronUpIcon
                                                                className={`h-4 w-4 transition-transform ${
                                                                    task.isOpen
                                                                        ? "rotate-180"
                                                                        : ""
                                                                }`}
                                                            />
                                                        </button>

                                                        {/* Title + status + renew */}
                                                        <div className="flex flex-col min-w-0 gap-1">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <h2 className="font-semibold break-words text-slate-800">
                                                                    {task.name}
                                                                </h2>

                                                                <span
                                                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-white shrink-0 ${
                                                                        task.status ===
                                                                        0
                                                                            ? "bg-red-500"
                                                                            : "bg-yellow-500"
                                                                    }`}
                                                                >
                                                                    {task.status !==
                                                                        null &&
                                                                        STATUSES[
                                                                            task
                                                                                .status
                                                                        ]}
                                                                </span>

                                                                {RENEWAL_STATE[
                                                                    task
                                                                        .taskRenewalState
                                                                ] === "Renew" &&
                                                                    isServiceStatusInProgress &&
                                                                    canWriteSteps && (
                                                                        <button
                                                                            onClick={() => {
                                                                                renewTask(
                                                                                    clientServiceId,
                                                                                    task.id,
                                                                                    task.assignedDate,
                                                                                );
                                                                            }}
                                                                            className="flex items-center px-3 py-2 space-x-1 text-xs border border-gray-200 rounded-md sm:text-sm shrink-0"
                                                                        >
                                                                            {
                                                                                RENEWAL_STATE[
                                                                                    task
                                                                                        .taskRenewalState
                                                                                ]
                                                                            }
                                                                        </button>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </th>

                                                {/* Notes */}
                                                <th className="px-2 py-3 align-middle first:pl-5 last:pr-5">
                                                    <div className="flex items-start min-w-0 gap-2">
                                                        <span className="shrink-0 pt-0.5 text-sm font-semibold text-slate-600">
                                                            Notes:
                                                        </span>

                                                        <div className="flex-1 min-w-0">
                                                            <StepNotesPreview
                                                                notes={task.notes}
                                                            />
                                                        </div>
                                                    </div>
                                                </th>

                                                {/* Assigned / Completed / Due */}
                                                <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-left">
                                                        Date assigned{" "}
                                                        <span className="font-medium text-sky-500">
                                                            {task.assignedDate !==
                                                            "0001-01-01T00:00:00"
                                                                ? formatTimestampDate(
                                                                      task.assignedDate,
                                                                  )
                                                                : "Not assigned"}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm font-medium text-left">
                                                        Completed{" "}
                                                        <span className="font-medium text-sky-500">
                                                            {task.completedDate !==
                                                            "0001-01-01T00:00:00"
                                                                ? formatTimestampDate(
                                                                      task.completedDate,
                                                                  )
                                                                : ""}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm font-medium text-left">
                                                        Due{" "}
                                                        <span
                                                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-md font-medium text-white mb-2 ${
                                                                task.dueDate !==
                                                                "0001-01-01T00:00:00"
                                                                    ? getBgColorFromDate(
                                                                          task.dueDate,
                                                                      )
                                                                    : "bg-slate-500"
                                                            }`}
                                                        >
                                                            {task.dueDate !==
                                                            "0001-01-01T00:00:00"
                                                                ? formatDate(
                                                                      task.dueDate,
                                                                      {
                                                                          dateStyle:
                                                                              "short",
                                                                      },
                                                                  )
                                                                : "Not assigned"}
                                                        </span>
                                                    </div>
                                                </th>

                                                {/* Assigned to / by / modified / completed */}
                                                <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                                                    <StepEmployeeDetails
                                                        step={task}
                                                        teamMembers={
                                                            subscriptionTeamMembers
                                                        }
                                                        formatUpdatedDate={formatDateTime}
                                                    />
                                                </th>

                                                {task.status === 1 &&
                                                    canWriteSteps && (
                                                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                                                        <div className="flex items-center justify-end">
                                                            <button
                                                                onClick={() => {
                                                                    setUpdateTaskModal(
                                                                        task,
                                                                    );
                                                                    setIsUpdateTaskModalOpen(
                                                                        true,
                                                                    );
                                                                }}
                                                                className="flex items-center px-3 py-2 mr-4 space-x-1 border border-gray-200 rounded-md"
                                                            >
                                                                <PencilIcon className="w-4 h-4" />
                                                                <h1 className="text-sm font-medium">
                                                                    Update{" "}
                                                                    {term(
                                                                        "step_singular",
                                                                    )}
                                                                </h1>
                                                            </button>
                                                        </div>
                                                    </th>
                                                )}

                                                {isServiceStatusInProgress &&
                                                    task.status !== 1 &&
                                                    canWriteSteps && (
                                                        <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                                                            <div className="flex items-center justify-end">
                                                                <button
                                                                    onClick={() =>
                                                                        onStartJob(
                                                                            task,
                                                                        )
                                                                    }
                                                                    className="flex items-center px-3 py-2 mr-4 space-x-1 border border-gray-200 rounded-md"
                                                                >
                                                                    <PencilIcon className="w-4 h-4" />
                                                                    <h1 className="text-sm font-medium">
                                                                        Start{" "}
                                                                        {term(
                                                                            "step_singular",
                                                                        )}
                                                                    </h1>
                                                                </button>
                                                            </div>
                                                        </th>
                                                    )}
                                            </tr>
                                        </thead>

                                        {/* Table body */}
                                        <AnimatePresence mode="wait">
                                            {task.isOpen && (
                                                <ClientServiceSubscriptionTaskChecklistItemTableBody
                                                    task={task}
                                                    onUpdateAllChecklistItems={
                                                        onUpdateAllChecklistItemsV2
                                                    }
                                                    loggedInEmployee={
                                                        loggedInEmployee
                                                    }
                                                    isServiceStatusInProgress={
                                                        isServiceStatusInProgress
                                                    }
                                                    updateClientService={
                                                        updateClientService
                                                    }
                                                    getNewJobs={getNewTasks}
                                                    shouldResetChecklist={
                                                        shouldResetChecklist
                                                    }
                                                    setShouldResetChecklist={
                                                        setShouldResetChecklist
                                                    }
                                                    canReadSteps={canReadSteps}
                                                    canWriteSteps={canWriteSteps}
                                                    canDeleteSteps={canDeleteSteps}
                                                />
                                            )}
                                        </AnimatePresence>
                                    </table>
                                </div>
                            </motion.div>
                        </div>
                    );
                })}

            <h2 className="text-lg font-bold text-green-500">
                Complete {term("step_plural")}
            </h2>
            {completedTasks &&
                completedTasks.map((task, index) => {
                    return (
                        <div
                            key={task.id ?? `completed-${index}`}
                            className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200"
                        >
                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: "auto" }}
                                exit={{ height: 0 }}
                                transition={{
                                    type: "linear",
                                    height: { duration: 0.2 },
                                }}
                                className={"overflow-x-auto overflow-y-hidden"}
                            >
                                <table className="w-full table-fixed">
                                    <thead>
                                        <tr>
                                            <th className="py-3 first:pl-5 whitespace-nowrap">
                                                <div className="flex items-center space-x-3">
                                                    <ChevronUpIcon
                                                        className={`h-4 w-4 ${
                                                            task.isOpen &&
                                                            "rotate-180"
                                                        }`}
                                                        onClick={() => {
                                                            const newTasks = [
                                                                ...completedTasks,
                                                            ];
                                                            newTasks[index] = {
                                                                ...newTasks[index],
                                                                isOpen: !newTasks[
                                                                    index
                                                                ].isOpen,
                                                            };
                                                            setCompletedTasks(
                                                                newTasks,
                                                            );
                                                        }}
                                                    />
                                                    <h2 className="font-semibold text-slate-800">
                                                        {task.name}
                                                    </h2>
                                                    <span className="inline-flex items-center rounded-full bg-green-500 px-2.5 py-0.5 text-xs font-medium text-white">
                                                        {task.status !== null &&
                                                            STATUSES[task.status]}
                                                    </span>
                                                    {RENEWAL_STATE[
                                                        task.taskRenewalState
                                                    ] === "Renew" &&
                                                        isServiceStatusInProgress &&
                                                        canWriteSteps && (
                                                            <button
                                                                onClick={() => {
                                                                    renewTask(
                                                                        clientServiceId,
                                                                        task.id,
                                                                        task.assignedDate,
                                                                    );
                                                                }}
                                                                className="flex items-center px-3 py-2 mr-4 space-x-1 border border-gray-200 rounded-md"
                                                            >
                                                                {
                                                                    RENEWAL_STATE[
                                                                        task
                                                                            .taskRenewalState
                                                                    ]
                                                                }
                                                            </button>
                                                        )}
                                                </div>
                                            </th>
                                            <th className="px-2 py-3 align-middle first:pl-5 last:pr-5">
                                                <div className="flex items-start min-w-0 gap-2">
                                                    <span className="shrink-0 pt-0.5 text-sm font-semibold text-slate-600">
                                                        Notes:
                                                    </span>

                                                    <div className="flex-1 min-w-0">
                                                        <StepNotesPreview
                                                            notes={task.notes}
                                                        />
                                                    </div>
                                                </div>
                                            </th>
                                            <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                                                <div className="text-sm font-medium text-left">
                                                    Assigned{" "}
                                                    <span className="font-medium text-sky-500">
                                                        {task.assignedDate !==
                                                        "0001-01-01T00:00:00"
                                                            ? formatTimestampDate(
                                                                  task.assignedDate,
                                                              )
                                                            : "Not assigned"}
                                                    </span>
                                                </div>
                                                <div className="text-sm font-medium text-left">
                                                    Completed{" "}
                                                    <span className="font-medium text-sky-500">
                                                        {task.completedDate !==
                                                        "0001-01-01T00:00:00"
                                                            ? formatTimestampDate(
                                                                  task.completedDate,
                                                              )
                                                            : ""}
                                                    </span>
                                                </div>

                                                <div className="text-sm font-medium text-left">
                                                    Due{" "}
                                                    <span className="font-medium text-sky-500">
                                                        {task.dueDate !==
                                                        "0001-01-01T00:00:00"
                                                            ? formatDate(
                                                                  task.dueDate,
                                                                  {
                                                                      dateStyle:
                                                                          "short",
                                                                  },
                                                              )
                                                            : "Not assigned"}
                                                    </span>
                                                </div>
                                            </th>
                                            <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                                                <StepEmployeeDetails
                                                    step={task}
                                                    teamMembers={
                                                        subscriptionTeamMembers
                                                    }
                                                    formatUpdatedDate={formatDateTime}
                                                />
                                            </th>
                                            {task.status === 1 &&
                                                canWriteSteps && (
                                                <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                                                    <div className="flex items-center justify-end">
                                                        {isServiceStatusInProgress && (
                                                            <button
                                                                onClick={() => {
                                                                    console.log(
                                                                        task,
                                                                    );
                                                                    setUpdateTaskModal(
                                                                        task,
                                                                    );
                                                                    setIsUpdateTaskModalOpen(
                                                                        true,
                                                                    );
                                                                }}
                                                                className="flex items-center px-3 py-2 mr-4 space-x-1 border border-gray-200 rounded-md"
                                                            >
                                                                <PencilIcon className="w-4 h-4" />
                                                                <h1 className="text-sm font-medium">
                                                                    Update{" "}
                                                                    {term(
                                                                        "step_singular",
                                                                    )}
                                                                </h1>
                                                            </button>
                                                        )}
                                                    </div>
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    {/* Table body */}
                                    <AnimatePresence mode="wait">
                                        {task.isOpen && (
                                            <ClientServiceSubscriptionTaskChecklistItemTableBody
                                                task={task}
                                                onUpdateAllChecklistItems={
                                                    onUpdateAllChecklistItemsV2
                                                }
                                                loggedInEmployee={loggedInEmployee}
                                                getNewJobs={getNewTasks}
                                                updateClientService={
                                                    updateClientService
                                                }
                                                shouldResetChecklist={
                                                    shouldResetChecklist
                                                }
                                                setShouldResetChecklist={
                                                    setShouldResetChecklist
                                                }
                                                canReadSteps={canReadSteps}
                                                canWriteSteps={canWriteSteps}
                                                canDeleteSteps={canDeleteSteps}
                                            />
                                        )}
                                    </AnimatePresence>
                                </table>
                            </motion.div>
                        </div>
                    );
                })}
        </div>
    );
};

export default ClientServiceSubscriptionTasksList;
